import React, { useState, useEffect, useRef } from "react";
import { Editor as ToastEditor } from "@toast-ui/editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import { Lock, Loader2, Menu, Settings, Timer, X, Share2, Share, History, Save, Pencil, Eye, Download, CreditCard, Minus, Plus } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { normalizeMarkdown } from "../utils/markdown";
import { fetchPlanFileBlob, openPlanDownload, requestPlanDownloadPath } from "../utils/planDownload";
import AuthButton from "../components/AuthButton";
import { apiFetch, confirmPayment, createPayment } from "../api";
import { openPaymentWidget } from "../yookassa";
import ExampleDownloadButton from "../components/ExampleDownloadButton";
import ProgressMenu from "../components/ProgressMenu";
import ServiceRatingForm from "../components/ServiceRatingForm";
import ReviewsSummary from "../components/ReviewsSummary";
import { PLAN_GENERATION_TYPES } from "../constants/planGenerationTypes";
import { toast } from "react-hot-toast";

const MARKDOWN_PLUGINS = [remarkGfm];

const MarkdownContent = React.memo(function MarkdownContent({ content }) {
  return (
    <div className="markdown text-slate-700 leading-relaxed">
      <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS}>{content}</ReactMarkdown>
    </div>
  );
});

export default function EditorPage({
  idea,
  setIdea,
  direction,
  setDirection,
  generationType,
  setGenerationType,
  loading,
  paid,
  paymentStatus,
  plan,
  currentPlan,
  planUuid,
  version,
  status,
  streamSections,
  streamSectionStatus,
  initialAnchorKey,
  onAnchorHandled,
  onBuy,
  onBack,
  children,
  user,
  onLogin,
  onAccount,
  onUserUpdate,
  showSettings,
  setShowSettings,
  useOwnFunds,
  setUseOwnFunds,
  ownFunds,
  setOwnFunds,
  onRegenerate,
  onRepeatRegenerate,
  onOpenVersion,
  onSaveContent,
  onExtraPaymentSuccess,
  onTitle,
  onShare,
  onShareSite,
  shareLoading,
  showReviewsEnabled = true,
}) {
  const statusLabels = {
    PROCESSING: { label: "генерируется", className: "bg-amber-50 text-amber-700" },
    DONE: { label: "черновик", className: "bg-emerald-50 text-emerald-700" },
    DRAFT: { label: "черновик", className: "bg-emerald-50 text-emerald-700" },
    ERROR: { label: "ошибка генерации", className: "bg-red-50 text-red-700" },
    NEED_REGENERATE: { label: "нужен повтор генерации", className: "bg-amber-50 text-amber-700" },
  };
  const paymentStatusLabels = {
    PAID: { label: "оплачен", className: "bg-emerald-50 text-emerald-700" },
    NOT_PAID: { label: "не оплачен", className: "bg-amber-50 text-amber-700" },
  };
  const ratingHideMs = 1000 * 60 * 60 * 24 * 60; // 2 months
  const ratingStorageKey = "serviceRatingHiddenUntil";
  const reviewsHideMs = 1000 * 60 * 60 * 24 * 7; // 1 week
  const reviewsStorageKey = "reviewsHiddenUntil";
  const notifyCtaDelayMs = 3 * 60 * 1000;
  const [nowTs, setNowTs] = useState(() => Date.now());

  useEffect(() => {
    document.title = `СоцПлан | ${direction || "Редактор"}`;
  }, [direction]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
    if (paid || typeof window === "undefined") {
      return undefined;
    }

    const handleCopy = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.clipboardData) {
        try {
          event.clipboardData.clearData();
        } catch (_) {}
      }
      toast.error("Копирование заблокировано до оплаты");
    };

    document.addEventListener("copy", handleCopy, true);

    return () => {
      document.removeEventListener("copy", handleCopy, true);
    };
  }, [paid]);

  const sections = [
    { title: "Резюме проекта", key: "summary" },
    { title: "Описание проекта/продукта/услуги", key: "description" },
    { title: "Маркетинговый анализ", key: "marketAnalysis" },
    { title: "Производственный план", key: "productionPlan" },
    { title: "Маркетинговый план", key: "marketingPlan" },
    { title: "Финансовые показатели и прогнозы", key: "financialPlan" },
    { title: "Анализ рисков", key: "risks" },
    { title: "Социальное влияние", key: "socialImpact" },
  ];

  const sectionTokenTargets = {
    summary: 300,
    description: 500,
    marketAnalysis: 500,
    productionPlan: 500,
    marketingPlan: 500,
    financialPlan: 500,
    risks: 500,
    socialImpact: 350,
  };

  const countTokens = (value) => {
    if (!value) return 0;
    const trimmed = String(value).trim();
    if (!trimmed) return 0;
    const matches = trimmed.match(/[A-Za-zА-Яа-яЁё0-9]+/g);
    return matches ? matches.length : 0;
  };

  const [showDownload, setShowDownload] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [draftPlan, setDraftPlan] = useState(null);
  const [savedTextSnapshot, setSavedTextSnapshot] = useState("");
  const [savingText, setSavingText] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [planPrice, setPlanPrice] = useState(1000);
  const [extraGenerationPrice, setExtraGenerationPrice] = useState(200);
  const [showExtraPurchase, setShowExtraPurchase] = useState(false);
  const [extraQuantity, setExtraQuantity] = useState(1);
  const [extraPaymentBusy, setExtraPaymentBusy] = useState(false);
  const [ratingHiddenUntil, setRatingHiddenUntil] = useState(null);
  const [reviewsHiddenUntil, setReviewsHiddenUntil] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [publicRatings, setPublicRatings] = useState(null);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsLoadingMore, setRatingsLoadingMore] = useState(false);
  const [ratingsError, setRatingsError] = useState("");
  const [showNotifyPrompt, setShowNotifyPrompt] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);
  const reviewsPageSize = 10;
  const lastAnchorRef = React.useRef(null);
  const canSharePlan = status === "DONE" && Boolean(planUuid);
  const reviewsHidden =
    !paid && showReviewsEnabled && reviewsHiddenUntil !== null && reviewsHiddenUntil > Date.now();
  const hasUnsavedTextChanges = Boolean(draftPlan) && JSON.stringify(draftPlan) !== savedTextSnapshot;
  const displayedPlan = hasUnsavedTextChanges ? draftPlan : plan;
  const totalRegenerations = 2 + (currentPlan?.extraGenerationTotal ?? 0);
  const usedRegenerations = Math.max(0, (currentPlan?.generationCount ?? 0) - 1);
  const availableRegenerations = Math.max(0, totalRegenerations - usedRegenerations);

  useEffect(() => {
    setDraftPlan(plan ? { ...plan } : null);
    setSavedTextSnapshot(JSON.stringify(plan || null));
    setEditingText(false);
  }, [plan, version]);

  useEffect(() => {
    if (!hasUnsavedTextChanges || typeof window === "undefined") return undefined;
    const warnBeforeUnload = (event) => {
      const message = "У вас есть не сохраненные изменения, при закрытии — изменения потеряются. Вы уверены, что хотите покинуть страницу?";
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasUnsavedTextChanges]);

  const loadVersions = async () => {
    if (!planUuid) return;
    setVersionsLoading(true);
    try {
      const res = await apiFetch(`/plan/${planUuid}/versions`, { planUuid, silent: true });
      if (res.ok) setVersions(await res.json());
    } finally {
      setVersionsLoading(false);
    }
  };

  const openVersions = () => {
    setShowVersions(true);
    loadVersions();
  };

  const saveText = async () => {
    if (!draftPlan || !onSaveContent) return;
    setSavingText(true);
    try {
      const saved = await onSaveContent(JSON.stringify(draftPlan));
      if (saved) {
        setSavedTextSnapshot(JSON.stringify(draftPlan));
        setEditingText(false);
        toast.success("Изменения сохранены");
      }
    } finally {
      setSavingText(false);
    }
  };

  useEffect(() => {
    if (showNotifyPrompt && user?.notifyOnPlanReady) {
      setShowNotifyPrompt(false);
    }
  }, [showNotifyPrompt, user]);

  useEffect(() => {
    setNowTs(Date.now());
    if (typeof window === "undefined") return undefined;
    if (status !== "PROCESSING" || user?.notifyOnPlanReady) return undefined;

    const startedAtRaw = currentPlan?.generationStartedAt;
    const startedAtMs = startedAtRaw ? Date.parse(startedAtRaw) : Number.NaN;
    if (!Number.isFinite(startedAtMs)) return undefined;

    const visibleAtMs = startedAtMs + notifyCtaDelayMs;
    const delayMs = visibleAtMs - Date.now();
    if (delayMs <= 0) return undefined;

    const timer = window.setTimeout(() => {
      setNowTs(Date.now());
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [currentPlan?.generationStartedAt, notifyCtaDelayMs, status, user?.notifyOnPlanReady]);

  const loadPublicRatings = async ({ offset = 0, append = false, skipIf } = {}) => {
    const shouldSkip = () => (typeof skipIf === "function" ? skipIf() : false);
    if (shouldSkip()) return;
    if (append) {
      setRatingsLoadingMore(true);
    } else {
      setRatingsLoading(true);
    }
    setRatingsError("");
    try {
      const params = new URLSearchParams({
        limit: String(reviewsPageSize),
        offset: String(offset),
      });
      const res = await apiFetch(`/ratings/public?${params.toString()}`, { silent: true });
      if (!res.ok) {
        if (!shouldSkip()) {
          setRatingsError("Не удалось загрузить отзывы");
        }
        return;
      }
      const data = await res.json();
      const moderatedData = {
        ...data,
        recent: Array.isArray(data?.recent)
          ? data.recent.filter((item) => item && item.moderated === true)
          : [],
      };
      if (!shouldSkip()) {
        setPublicRatings((prev) => {
          if (!append || !prev) return moderatedData;
          const existing = Array.isArray(prev.recent) ? prev.recent : [];
          const incoming = Array.isArray(moderatedData.recent) ? moderatedData.recent : [];
          const seen = new Set(existing.map((item) => item.id));
          const merged = existing.concat(incoming.filter((item) => !seen.has(item.id)));
          return { ...moderatedData, recent: merged };
        });
      }
    } catch (err) {
      if (!shouldSkip()) {
        setRatingsError("Не удалось загрузить отзывы");
      }
    } finally {
      if (!shouldSkip()) {
        if (append) {
          setRatingsLoadingMore(false);
        } else {
          setRatingsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const handler = (event) => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!paid || typeof window === "undefined") {
      setRatingHiddenUntil(null);
      return;
    }
    const stored = localStorage.getItem(ratingStorageKey);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!Number.isNaN(ts) && ts > Date.now()) {
        setRatingHiddenUntil(ts);
      } else {
        localStorage.removeItem(ratingStorageKey);
      }
    }
  }, [paid]);

  useEffect(() => {
    if (paid || !showReviewsEnabled || typeof window === "undefined") {
      setReviewsHiddenUntil(null);
      return;
    }
    const stored = localStorage.getItem(reviewsStorageKey);
    if (stored) {
      const ts = parseInt(stored, 10);
      if (!Number.isNaN(ts) && ts > Date.now()) {
        setReviewsHiddenUntil(ts);
      } else {
        localStorage.removeItem(reviewsStorageKey);
      }
    }
  }, [paid, showReviewsEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (paid || !showReviewsEnabled || reviewsHidden) {
      setPublicRatings(null);
      setRatingsLoading(false);
      setRatingsLoadingMore(false);
      setRatingsError("");
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      await loadPublicRatings({ offset: 0, append: false, skipIf: () => cancelled });
    })();
    return () => {
      cancelled = true;
    };
  }, [paid, showReviewsEnabled, reviewsHidden]);

  // Load dynamic price from API
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/config', { silent: true });
        if (res.ok) {
          const data = await res.json();
          if (typeof data.planPrice === 'number') setPlanPrice(data.planPrice);
          if (typeof data.extraGenerationPrice === 'number') setExtraGenerationPrice(data.extraGenerationPrice);
        }
      } catch {}
    })();
  }, []);

  const startExtraPayment = async () => {
    if (!planUuid || !user) {
      onLogin?.();
      return;
    }
    setExtraPaymentBusy(true);
    try {
      const payment = await createPayment(planUuid, extraGenerationPrice * extraQuantity, {
        purchaseType: "EXTRA_GENERATIONS",
        quantity: extraQuantity,
      });
      const token = payment?.confirmation?.confirmation_token ?? payment?.confirmationToken ?? payment?.confirmation_token;
      if (!token) throw new Error("Не получен токен оплаты");
      openPaymentWidget(token, async () => {
        let confirmed = false;
        for (let attempt = 0; attempt < 8; attempt += 1) {
          const result = await confirmPayment(payment.id);
          if (result?.confirmed) {
            confirmed = true;
            break;
          }
          const status = String(result?.status || "").toLowerCase();
          if (["canceled", "failed"].includes(status)) break;
          await new Promise((resolve) => window.setTimeout(resolve, 1000));
        }
        if (!confirmed) {
          toast.error("Платёж ещё не подтверждён. Попробуйте открыть настройки через несколько секунд.");
          return;
        }
        setShowExtraPurchase(false);
        onExtraPaymentSuccess?.();
      });
    } catch (_) {
      toast.error("Не удалось начать оплату");
    } finally {
      setExtraPaymentBusy(false);
    }
  };

  const downloadFile = async (type) => {
    if (!planUuid) return;
    setDownloading(type);
    try {
      const path = await requestPlanDownloadPath(planUuid, type, version);
      if (path) {
        const downloaded = await openPlanDownload(path, `plan_${planUuid}.${type}`);
        if (!downloaded) {
          toast.error("Не удалось создать файл для скачивания. Пожалуйста, обратитесь в службу поддержки");
          return;
        }
        setShowDownload(false);
        return;
      }
      const blob = await fetchPlanFileBlob(planUuid, type, version);
      if (!blob) {
        toast.error("Не удалось создать файл для скачивания. Пожалуйста, обратитесь в службу поддержки");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `plan_${planUuid}.${type}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowDownload(false);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let cancelled = false;
    const maxAttempts = 30;
    const retryDelayMs = 120;

    const resolveAnchorKey = () => {
      if (initialAnchorKey) return initialAnchorKey;
      return window.location.hash.replace(/^#/, "");
    };

    const scrollWithRetries = (anchorKey) => {
      if (!anchorKey) return;
      if (lastAnchorRef.current === anchorKey) return;
      let attempts = 0;

      const tryScrollToAnchor = () => {
        if (cancelled) return;
        const el = document.getElementById(anchorKey);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          onAnchorHandled?.(anchorKey);
          lastAnchorRef.current = anchorKey;
          return;
        }
        attempts += 1;
        if (attempts < maxAttempts) {
          window.setTimeout(tryScrollToAnchor, retryDelayMs);
        }
      };

      window.requestAnimationFrame(() => {
        window.setTimeout(tryScrollToAnchor, 0);
      });
    };

    const anchorKey = resolveAnchorKey();
    if (anchorKey) {
      scrollWithRetries(anchorKey);
    }

    return () => {
      cancelled = true;
    };
  }, [initialAnchorKey, onAnchorHandled, planUuid]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleHashChange = () => {
      const anchorKey = window.location.hash.replace(/^#/, "");
      if (!anchorKey) return;
      const el = document.getElementById(anchorKey);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        onAnchorHandled?.(anchorKey);
        lastAnchorRef.current = anchorKey;
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [onAnchorHandled, planUuid]);

  const handleMenuClick = (key) => {
    if (typeof window !== "undefined") {
      const nextUrl = `${window.location.pathname}#${key}`;
      window.history.replaceState(window.history.state, "", nextUrl);
    }
    const el = document.getElementById(key);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const menuItems = [
    { title: "Титульный лист", onClick: onTitle },
    { title: "Резюме проекта", key: "summary" },
    { title: "Описание проекта/продукта/услуги", key: "description" },
    { title: "Маркетинговый анализ", key: "marketAnalysis" },
    { title: "Финансовые показатели и прогнозы", key: "financialPlan" },
    { title: "Анализ рисков", key: "risks" },
    { title: "Социальное влияние", key: "socialImpact" },
  ];
  const hasMenuAction = (item) => Boolean(item.onClick || item.key);
  const handleMenuItem = (item, closeAfter = false) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.key) {
      handleMenuClick(item.key);
    }
    if (closeAfter) {
      setMobileMenuOpen(false);
    }
  };
  const ratingHidden = paid && ratingHiddenUntil !== null && ratingHiddenUntil > Date.now();
  const hideRatingPrompt = () => {
    if (!paid) {
      setShowRatingModal(false);
      return;
    }
    const until = Date.now() + ratingHideMs;
    setRatingHiddenUntil(until);
    if (typeof window !== "undefined") {
      localStorage.setItem(ratingStorageKey, String(until));
    }
    setShowRatingModal(false);
  };
  const handleRatingSubmitted = () => {
    hideRatingPrompt();
  };
  const hideReviewsPrompt = () => {
    if (paid || !showReviewsEnabled) return;
    const until = Date.now() + reviewsHideMs;
    setReviewsHiddenUntil(until);
    if (typeof window !== "undefined") {
      localStorage.setItem(reviewsStorageKey, String(until));
    }
  };
  const mobileSummary = (() => {
    const distribution = publicRatings?.distribution || {};
    const distCounts = [1, 2, 3, 4, 5].map((value) => Number(distribution[value] ?? distribution[String(value)] ?? 0) || 0);
    const totalFromDist = distCounts.reduce((sum, value) => sum + value, 0);
    const totalReviews = Number(publicRatings?.totalReviews ?? totalFromDist) || 0;
    const averageRating = Number(publicRatings?.averageRating ?? 0) || 0;
    return { totalReviews, averageRating };
  })();
  const loadedCommentReviews = Array.isArray(publicRatings?.recent) ? publicRatings.recent.length : 0;
  const totalCommentReviews = Number(
    publicRatings?.totalCommentReviews ?? loadedCommentReviews
  );
  const hasMoreCommentReviews = loadedCommentReviews < totalCommentReviews;
  const handleLoadMoreReviews = () => {
    if (ratingsLoading || ratingsLoadingMore) return;
    if (!publicRatings) return;
    if (!hasMoreCommentReviews) return;
    loadPublicRatings({ offset: loadedCommentReviews, append: true });
  };
  const streamingData = streamSections || {};
  const sectionStatusMap = streamSectionStatus || {};
  const isProcessing = status === "PROCESSING";
  const isFinalizing = (sectionStatusMap["socialImpact"] === "done") && status !== "DONE";
  const productionPlanReady = sectionStatusMap["productionPlan"] === "done";
  const generationStartedAtMs = currentPlan?.generationStartedAt
    ? Date.parse(currentPlan.generationStartedAt)
    : Number.NaN;
  const shouldShowNotifyCta = isProcessing &&
    (!user || !user.notifyOnPlanReady) &&
    Number.isFinite(generationStartedAtMs) &&
    nowTs - generationStartedAtMs >= notifyCtaDelayMs;
  const shouldShowPurchaseCta = !paid && status !== "ERROR" && (!isProcessing || productionPlanReady);
  const shouldShowPaidProcessingCta = paid && isProcessing;

  const handleEnableNotifications = async () => {
    if (!user) {
      setShowNotifyPrompt(false);
      onLogin?.();
      return;
    }
    if (notifySaving) return;
    setNotifySaving(true);
    try {
      const payload = {
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        middleName: user.middleName ?? null,
        phone: user.phone ?? null,
        notifyOnPlanReady: true,
      };
      const res = await apiFetch('/user', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        toast.error('Не удалось включить уведомления');
        return;
      }
      const data = await res.json();
      localStorage.setItem('user', JSON.stringify(data));
      onUserUpdate?.(data);
      setShowNotifyPrompt(false);
    } catch {
      toast.error('Не удалось включить уведомления');
    } finally {
      setNotifySaving(false);
    }
  };
  const handleNotifyCtaClick = () => {
    if (typeof window !== "undefined" && typeof window.ym === "function") {
      window.ym(104041659, "reachGoal", "TOO_LONG");
    }
    setShowNotifyPrompt(true);
  };

  // Debug: view-layer decision logs are disabled
  // try {
  //   ["summary", "description"].forEach((k) => {
  //     const v = (streamingData && streamingData[k]) || "";
  //     const idx = sections.findIndex((s) => s.key === k);
  //     const isLocked = !paid && idx >= 4;
  //     const shouldShow = isProcessing && typeof v === "string" && v.length > 0 && !isLocked;
  //     console.log(
  //       `[Plan stream][view][${k}] len=${v.length} shouldShow=${shouldShow} status=${status} paid=${paid}`
  //     );
  //   });
  // } catch (_) {}
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Topbar */}
      <div className="sticky sticky-safe-top z-30 bg-white/80 backdrop-blur">
        <header className="border-b bg-white/80 backdrop-blur">
          <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Открыть меню"
                aria-expanded={mobileMenuOpen}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3 text-sm text-slate-600">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onBack();
                  }}
                  className="font-medium text-slate-700 hover:text-slate-900"
                >
                  Главная
                </button>
                <span className="text-slate-400">→</span>
                <span className="font-semibold text-slate-900">Черновик</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {onShareSite && (
                <button
                  type="button"
                  onClick={onShareSite}
                  className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
                  aria-label="Поделиться сайтом"
                >
                  <Share2 className="h-4 w-4" />
                  <span className="hidden md:inline">Поделиться</span>
                </button>
              )}
              <AuthButton user={user} onLogin={onLogin} onAccount={onAccount} />
            </div>
          </div>
        </header>
        <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3">
          <ProgressMenu
            status={status}
            sectionStatusMap={sectionStatusMap}
            planUuid={planUuid}
            variant="plain"
          />
        </div>
        {paid && !ratingHidden && (
          <div className="editor-service-rating md:hidden border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Оценка сервиса</div>
                <p className="text-xs text-slate-600">Напишите пару строк, чтобы сервис стал удобнее</p>
              </div>
              <button
                type="button"
                onClick={hideRatingPrompt}
                aria-label="Скрыть блок оценки"
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowRatingModal(true)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Заполнить отзыв
            </button>
          </div>
        )}
        {!paid && showReviewsEnabled && !reviewsHidden && (
          <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-baseline gap-2 text-sm font-semibold text-slate-900">
                  <span>Отзывы пользователей</span>
                  <span className="text-xs font-medium text-slate-600">
                    {ratingsLoading
                      ? "Загружаем отзывы..."
                      : `${mobileSummary.totalReviews} отзывов • ${
                          mobileSummary.averageRating > 0 ? mobileSummary.averageRating.toFixed(1) : "—"
                        }/5`}
                  </span>
                </div>
                {ratingsError && !ratingsLoading && (
                  <div className="mt-1 text-[11px] text-amber-600">{ratingsError}</div>
                )}
              </div>
              <button
                type="button"
                onClick={hideReviewsPrompt}
                aria-label="Скрыть отзывы"
                className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowReviewsModal(true)}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Смотреть отзывы
            </button>
          </div>
        )}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-slate-200 bg-white px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">Предпросмотр</div>
            <div className="mt-2 space-y-1">
              {menuItems.map((item, index) => (
                <div key={index}>
                  {hasMenuAction(item) ? (
                    <button
                      type="button"
                      onClick={() => handleMenuItem(item, true)}
                      className="block w-full rounded-md px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    >
                      {item.title}
                    </button>
                  ) : (
                    <div className="px-2 py-2 text-sm text-slate-700">{item.title}</div>
                  )}
                </div>
              ))}
            </div>
            {onShareSite && (
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onShareSite();
                }}
                className="mt-3 inline-flex w-full items-center justify-start gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 hover:text-slate-900"
              >
                <Share2 className="h-4 w-4" />
                Поделиться
              </button>
            )}
          </div>
        )}
      </div>

      {/* Layout */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-4 py-5 md:grid-cols-12 md:gap-6 md:py-6">
        {/* Sidebar */}
        <aside className="hidden md:col-span-3 md:block md:sticky md:top-[4.5rem] md:self-start space-y-3">
          <div className="rounded-xl border bg-white p-3 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto">
            <div className="mb-2 text-sm font-semibold text-slate-900">Предпросмотр</div>
            {menuItems.map((t, i) => (
              <div
                key={i}
                onClick={hasMenuAction(t) ? () => handleMenuItem(t) : undefined}
                className={`py-2 text-sm text-slate-700 ${
                  hasMenuAction(t)
                    ? "cursor-pointer hover:text-slate-900"
                    : "cursor-default"
                }`}
              >
                {t.title}
              </div>
            ))}
          </div>
          <ProgressMenu
            status={status}
            sectionStatusMap={sectionStatusMap}
            planUuid={planUuid}
          />
          {paid && !ratingHidden && (
            <div className="editor-service-rating mt-3 hidden md:block">
              <ServiceRatingForm
                planUuid={planUuid}
                onClose={hideRatingPrompt}
                onSubmitted={handleRatingSubmitted}
              />
            </div>
          )}
          {!paid && showReviewsEnabled && !reviewsHidden && (
            <div className="mt-3 hidden md:block">
              {ratingsLoading ? (
                <div className="rounded-xl border bg-white p-4 text-sm text-slate-600 shadow-sm">
                  Загружаем отзывы...
                </div>
              ) : (
                <ReviewsSummary
                  data={publicRatings}
                  reviewMaxLines={4}
                  showReviewText={false}
                  showReviewCard={false}
                  onClose={hideReviewsPrompt}
                  onShowAll={() => setShowReviewsModal(true)}
                />
              )}
            </div>
          )}
        </aside>

        {/* Content */}
        <main className="col-span-12 md:col-span-9">
          <div className="rounded-xl border bg-white p-5">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {!paid && (
                  <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs text-slate-600">
                    <Lock className="h-4 w-4" /> Часть разделов скрыта до оплаты
                  </div>
                )}
                {isProcessing && (
                  <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs text-slate-600">
                    <Timer className="h-4 w-4" /> Среднее время генерации - 5 минут
                  </div>
                )}
              </div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                {onShare && (
                  <button
                    type="button"
                    onClick={onShare}
                    disabled={!canSharePlan || shareLoading}
                    className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                    title="Поделиться доступом к бизнес-плану"
                  >
                    {shareLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share className="h-4 w-4" />}
                    Поделиться доступом
                  </button>
                )}
                <button
                  type="button"
                  onClick={openVersions}
                  disabled={!planUuid}
                  className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
                  title="Версии бизнес-плана"
                >
                  <History className="h-5 w-5" /> Версии
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  disabled={status === "PROCESSING"}
                  className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
                >
                  <Settings className="h-5 w-5" />
                  <span>Повтор генерации</span>
                </button>
              </div>
            </div>
            {status === "NEED_REGENERATE" && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm">
                Отображается старая версия, нажмите "Повторить генерацию", это бесплатно!
              </div>
            )}
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <h1 className="text-xl font-semibold text-slate-900">
                {direction || "Черновик бизнес‑плана"}
              </h1>
              <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    statusLabels[status]?.className || "bg-slate-100 text-slate-700"
                  }`}
                >
                  {statusLabels[status]?.label || status}
                  {status === "PROCESSING" && <Loader2 className="h-3 w-3 animate-spin" />}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    paymentStatusLabels[paymentStatus]?.className || "bg-slate-100 text-slate-700"
                  }`}
                >
                  {paymentStatusLabels[paymentStatus]?.label || paymentStatus}
                </span>
                {status === "NEED_REGENERATE" && (
                  <button
                    onClick={onRepeatRegenerate}
                    disabled={status === "PROCESSING"}
                    className="inline-flex h-8 items-center justify-center rounded-md bg-blue-600 px-3 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    Повторить генерацию
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={onTitle}
              className="mb-4 inline-flex w-full h-10 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow hover:bg-blue-700"
            >
              Титульный лист
            </button>

            {/* Skeleton/Content */}
            {status === "ERROR" ? (
              <p className="text-sm text-red-500">Не удалось сгенерировать бизнес-план.</p>
            ) : (
              <div className="space-y-6">
                {sections.map((s, idx) => {
                  const streamingRaw = streamingData[s.key] || "";
                  const sectionStatus = sectionStatusMap[s.key];
                  const isLockedSection = !paid && idx >= 4;
                  const isImportant = s.key === "summary" || s.key === "description";
                  // While streaming, be more permissive for important sections. For non-important
                  // sections require PROCESSING to avoid flicker.
                  const hasStreamingContent =
                    typeof streamingRaw === "string" && (isImportant ? streamingRaw.length > 0 : (isProcessing && streamingRaw.trim() !== ""));
                  const shouldShowStreamingContent = hasStreamingContent && !isLockedSection;
                  const skeleton = (
                    <>
                      <div className="h-3 w-full rounded bg-slate-100 animate-pulse" />
                      <div className="h-3 w-5/6 rounded bg-slate-100 animate-pulse" />
                      <div className="h-3 w-3/4 rounded bg-slate-100 animate-pulse" />
                    </>
                  );
                  let displayStreaming = streamingRaw;
                  if (isImportant && typeof displayStreaming === 'string') {
                    // Decode common JSON escapes possibly appearing double-escaped in chunks
                    const decodeEscapes = (s) => {
                      try {
                        // First collapse double-escaped sequences, then single
                        return s
                          .replace(/\\\\n/g, '\n')
                          .replace(/\\n/g, '\n')
                          .replace(/\\\\r/g, '\r')
                          .replace(/\\r/g, '\r')
                          .replace(/\\\\t/g, '\t')
                          .replace(/\\t/g, '\t')
                          .replace(/\\\\\"/g, '"')
                          .replace(/\\\"/g, '"');
                      } catch {
                        return s;
                      }
                    };
                    displayStreaming = decodeEscapes(displayStreaming)
                      // Remove BOM/zero-width/nbsp at the start that can break headings
                      .replace(/^[\uFEFF\u200B\u00A0]+/, '');
                    // Strip a stray leading quote if present due to partial tokenization
                    if (displayStreaming.startsWith('"') && !displayStreaming.startsWith('""')) {
                      displayStreaming = displayStreaming.slice(1);
                    }
                  }
                  const progressSource = (() => {
                    if (typeof displayStreaming === "string" && displayStreaming.trim().length > 0) {
                      return displayStreaming;
                    }
                    if (typeof streamingRaw === "string" && streamingRaw.trim().length > 0) {
                      return streamingRaw;
                    }
                    // A new generation must never inherit progress from the saved
                    // section of a previous version.
                    if (!isProcessing && typeof displayedPlan?.[s.key] === "string") {
                      return displayedPlan?.[s.key];
                    }
                    return "";
                  })();
                  const generatedTokens = countTokens(progressSource);
                  const targetTokens = sectionTokenTargets[s.key] || 400;
                  const progressPercent =
                    generatedTokens <= 0 || targetTokens <= 0
                      ? 0
                      : Math.min(99, Math.max(1, Math.floor((generatedTokens / targetTokens) * 100)));
                  const streamingContent = (
                    <MarkdownContent
                      content={isImportant ? normalizeMarkdown(displayStreaming) : normalizeMarkdown(streamingRaw)}
                    />
                  );
                  const finalContent = (
                    <MarkdownContent content={normalizeMarkdown(displayedPlan?.[s.key] || "")} />
                  );
                  const hasFinalContent = Boolean(displayedPlan?.[s.key]) && String(displayedPlan?.[s.key]).trim().length > 0;
                  // After PROCESSING finished, allow important sections to render the latest
                  // streamed content as a fallback until final plan content is available.
                  const showStreamingFallback = !isProcessing && !isLockedSection && isImportant && !hasFinalContent && typeof streamingRaw === 'string' && streamingRaw.length > 0;
                  return (
                    <div key={s.key} id={s.key} className="space-y-2 scroll-mt-[5rem]">
                      <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                        {s.title}
                        {isProcessing && sectionStatus === "in_progress" && (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                            {progressPercent === 0 ? (
                              <span className="text-xs font-medium shimmer-text">ИИ думает..</span>
                            ) : (
                              <span className="text-xs font-medium text-slate-500">{progressPercent}%</span>
                            )}
                          </span>
                        )}
                        {isProcessing && sectionStatus === "done" && (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                            сгенерировано
                          </span>
                        )}
                        {!loading && displayedPlan && isLockedSection && (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                            будет доступен после оплаты
                          </span>
                        )}
                      </h2>
                      {editingText ? (
                        <VisualMarkdownEditor
                          value={draftPlan?.[s.key] || ""}
                          onChange={(value) => setDraftPlan((previous) => ({ ...previous, [s.key]: value }))}
                          placeholder={`Введите раздел «${s.title}»`}
                        />
                      ) : status === "PROCESSING"
                        ? (shouldShowStreamingContent ? streamingContent : skeleton)
                        : isLockedSection
                        ? skeleton
                        : (showStreamingFallback ? streamingContent : finalContent)}
                    </div>
                  );
                })}
                <div id="appendices" className="scroll-mt-[5rem]" />
                {loading && (
                  <p className="text-xs text-slate-500">
                    Генерация продолжается в фоновом режиме…
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Sticky CTA */}
            {shouldShowPurchaseCta && (
              <div className="sticky bottom-4 mt-4 flex flex-col gap-2 md:items-end">
                {shouldShowNotifyCta && (
                  <button
                    type="button"
                    onClick={handleNotifyCtaClick}
                    className="h-11 rounded-lg bg-gray-500 px-5 text-white font-semibold shadow hover:bg-gray-600 w-full md:w-[250px]"
                  >
                    Надоело ждать? Нажми
                  </button>
                )}
                <ExampleDownloadButton paid={paid} className="!w-full md:!w-[250px]" />
                <button
                  onClick={onBuy}
                  className="h-11 rounded-lg bg-blue-600 px-5 text-white font-semibold shadow hover:bg-blue-700 w-full md:w-[250px]"
                >
                  {`Купить за ${new Intl.NumberFormat('ru-RU').format(planPrice)} ₽`}
                </button>
                {isFinalizing && (
                  <button
                    type="button"
                    disabled
                    className="h-11 rounded-lg bg-slate-100 px-5 text-slate-600 font-semibold flex items-center gap-2 cursor-wait"
                    title="Вносим последние правки"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" /> Вносим последние правки
                  </button>
                )}
              </div>
            )}
            {shouldShowPaidProcessingCta && (
              <div className="sticky bottom-4 mt-4 flex flex-col gap-2 md:items-end">
                {shouldShowNotifyCta && (
                  <button
                    type="button"
                    onClick={handleNotifyCtaClick}
                    className="h-11 rounded-lg bg-gray-500 px-5 text-white font-semibold shadow hover:bg-gray-600 w-full md:w-[250px]"
                  >
                    Надоело ждать? Нажми
                  </button>
                )}
                {isFinalizing && (
                  <button
                    type="button"
                    disabled
                    className="h-11 rounded-lg bg-slate-100 px-5 text-slate-600 font-semibold flex items-center gap-2 cursor-wait w-full md:w-[250px]"
                    title="Вносим последние правки"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" /> Вносим последние правки
                  </button>
                )}
              </div>
            )}
          {paid && !["PROCESSING", "ERROR"].includes(status) && (
            <div className="sticky bottom-4 z-20 mt-4 flex justify-end gap-2">
              {isFinalizing && (
                <button
                  type="button"
                  disabled
                  className="h-11 rounded-lg bg-slate-100 px-5 text-slate-600 font-semibold flex items-center gap-2 cursor-wait"
                  title="Вносим последние правки"
                >
                  <Loader2 className="h-4 w-4 animate-spin" /> Вносим последние правки
                </button>
              )}
              {hasUnsavedTextChanges && (
                <button
                  type="button"
                  onClick={saveText}
                  disabled={savingText}
                  aria-label="Сохранить изменения"
                  title="Сохранить изменения"
                  className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-lg !bg-emerald-600 !opacity-100 px-0 text-sm font-semibold text-white shadow hover:!bg-emerald-700 disabled:cursor-not-allowed disabled:!bg-emerald-600 md:w-auto md:px-4"
                >
                  {savingText ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="hidden md:inline">Сохранить</span>
                </button>
              )}
              {status === "DONE" && (
                <button
                  type="button"
                  onClick={() => setEditingText((current) => !current)}
                  aria-label={editingText ? "Режим просмотра" : "Редактировать текст"}
                  title={editingText ? "Режим просмотра" : "Редактировать текст"}
                  className="inline-flex h-11 w-11 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-emerald-600 px-0 text-sm font-semibold text-white shadow hover:bg-emerald-700 md:w-auto md:px-4"
                >
                  {editingText ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  <span className="hidden md:inline">
                    {editingText ? "Режим просмотра" : "Редактировать текст"}
                  </span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowDownload(true)}
                aria-label="Скачать"
                title="Скачать"
                className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-lg !bg-blue-600 !opacity-100 px-0 text-white font-semibold shadow hover:!bg-blue-700 md:w-auto md:px-5"
              >
                <Download className="h-4 w-4" />
                <span className="hidden md:inline">Скачать</span>
              </button>
            </div>
          )}
        </main>
      </div>

      {shouldShowNotifyCta && !shouldShowPurchaseCta && !shouldShowPaidProcessingCta && (
        <button
          type="button"
          onClick={handleNotifyCtaClick}
          className="fixed left-4 right-4 bottom-6 z-30 inline-flex h-11 items-center justify-center rounded-lg bg-gray-500 px-5 text-white font-semibold shadow hover:bg-gray-600 sm:left-auto sm:right-6 sm:w-auto"
        >
          Надоело ждать? Нажми
        </button>
      )}

      {showNotifyPrompt && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
          onClick={() => setShowNotifyPrompt(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-lg font-semibold">Настроить уведомления</div>
              <button
                type="button"
                onClick={() => setShowNotifyPrompt(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Закрыть"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {user ? (
              <>
                <p className="mt-3 text-sm text-slate-600">
                  Включить уведомление о завершении генерации на e-mail. Никакого спама, только уведомление.
                </p>
                <button
                  type="button"
                  onClick={handleEnableNotifications}
                  disabled={notifySaving}
                  className="mt-4 inline-flex w-full h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white font-semibold shadow hover:bg-blue-700 disabled:opacity-50"
                >
                  {notifySaving ? 'Сохраняем…' : 'Включить'}
                </button>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-slate-600">
                  Зарегистрированным пользователям доступно уведомление о завершении генерации. Присоединяйтесь!
                </p>
                <button
                  type="button"
                  onClick={() => {
                    onLogin?.();
                  }}
                  className="mt-4 inline-flex w-full h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white font-semibold shadow hover:bg-blue-700"
                >
                  Зарегистрироваться / Войти
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {showRatingModal && paid && !ratingHidden && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <ServiceRatingForm
              planUuid={planUuid}
              onClose={() => setShowRatingModal(false)}
              onSubmitted={handleRatingSubmitted}
              compact
            />
          </div>
        </div>
      )}

      {showReviewsModal && !paid && showReviewsEnabled && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
          onClick={() => setShowReviewsModal(false)}
        >
          <div
            className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowReviewsModal(false)}
              aria-label="Закрыть отзывы"
              className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            {ratingsLoading ? (
              <div className="text-sm text-slate-600">Загружаем отзывы...</div>
            ) : (
              <ReviewsSummary
                data={publicRatings}
                reviewMaxLines={10}
                className="border-0 p-0 shadow-none"
                onLoadMore={handleLoadMoreReviews}
                hasMore={hasMoreCommentReviews}
                loadingMore={ratingsLoadingMore}
              />
            )}
            {ratingsError && !ratingsLoading && (
              <div className="mt-2 text-xs text-amber-600">{ratingsError}</div>
            )}
          </div>
        </div>
      )}

      {showVersions && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal aria-label="Версии бизнес-плана">
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <button type="button" onClick={() => setShowVersions(false)} aria-label="Закрыть версии" className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-semibold text-slate-900">Версии бизнес-плана</h2>
            <p className="mt-1 text-sm text-slate-500">Новые версии находятся выше в списке.</p>
            <div className="mt-4 max-h-[60vh] space-y-2 overflow-y-auto">
              {versionsLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Загружаем версии…</div>
              ) : versions.length === 0 ? (
                <div className="py-4 text-sm text-slate-500">Версий пока нет.</div>
              ) : versions.map((item) => (
                <div key={item.version} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div>
                    <div className="font-medium text-slate-900">Версия {item.version}{item.version === version ? " — открыта" : ""}</div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      Изменена: {item.updatedAt ? new Date(item.updatedAt).toLocaleString("ru-RU") : "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowVersions(false); onOpenVersion?.(item.version); }}
                    className="rounded-md border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Открыть
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showExtraPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal aria-label="Повтор генерации">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <button type="button" onClick={() => setShowExtraPurchase(false)} className="absolute right-3 top-3 rounded-full p-1 text-slate-400 hover:bg-slate-100" aria-label="Закрыть"><X className="h-5 w-5" /></button>
            <h2 className="text-lg font-semibold text-slate-900">Повтор генерации</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">Позволяют создать новые варианты бизнес-плана, не изменяя уже сохранённые версии.</p>
            <div className="mt-5 flex items-center justify-center gap-4">
              <button type="button" onClick={() => setExtraQuantity((value) => Math.max(1, value - 1))} className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50" aria-label="Уменьшить количество"><Minus className="h-5 w-5" /></button>
              <input type="number" min="1" max="100" value={extraQuantity} onChange={(event) => setExtraQuantity(Math.min(100, Math.max(1, Number(event.target.value) || 1)))} className="w-20 rounded-lg border px-3 py-2 text-center text-lg font-semibold" />
              <button type="button" onClick={() => setExtraQuantity((value) => Math.min(100, value + 1))} className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50" aria-label="Увеличить количество"><Plus className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700"><span>Стоимость</span><strong>{new Intl.NumberFormat("ru-RU").format(extraGenerationPrice * extraQuantity)} ₽</strong></div>
            <button type="button" onClick={startExtraPayment} disabled={extraPaymentBusy} className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
              {extraPaymentBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Оплатить
            </button>
          </div>
        </div>
      )}

      {showSettings && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Настройки бизнес‑плана</div>
            <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3">
              <div className="text-sm text-slate-700">
                <div className="font-medium text-slate-900">Повтор генерации</div>
                <div>
                  Доступно {availableRegenerations} из {totalRegenerations}
                </div>
              </div>
              <button type="button" onClick={() => setShowExtraPurchase(true)} disabled={!paid} title={paid ? undefined : "Доступно после оплаты бизнес-плана"} className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                Увеличить
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">Бизнес‑направление</label>
                <input
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  placeholder="Напр.: Кондитерская / Парикмахерская / Груминг"
                  className="w-full rounded-lg border px-3 py-2 text-[15px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">Направление генерации</label>
                <select
                  value={generationType}
                  onChange={(e) => setGenerationType(e.target.value)}
                  disabled
                  className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-[15px] text-slate-500"
                >
                  {PLAN_GENERATION_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[12px] text-slate-500">
                  Это поле фиксируется при создании плана и не меняется при повторе генерации.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-slate-700">Опишите вашу идею</label>
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 text-[15px] min-h-[120px]"
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={useOwnFunds}
                    onChange={(e) => setUseOwnFunds(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Учитывать собственные средства в бизнес плане
                </label>
                {useOwnFunds && (
                  <input
                    type="number"
                    value={ownFunds}
                    onChange={(e) => setOwnFunds(e.target.value)}
                    placeholder="Сумма в ₽"
                    className="mt-2 w-full rounded-lg border px-3 py-2 text-[15px]"
                  />
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={onRegenerate}
                disabled={!idea}
                className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-5 text-white text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                Сохранить и перегенерировать
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="h-11 rounded-lg border px-4 text-[15px]"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {showDownload && planUuid && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl relative">
            <button
              onClick={() => setShowDownload(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="text-lg font-semibold">Скачать файл</div>
            {isProcessing && (
              <p className="mt-2 text-sm text-slate-600">
                Генерация еще в процессе. Для скачивания файлов дождитесь завершения
              </p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => downloadFile('pdf')}
                disabled={isProcessing || downloading === 'pdf'}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> PDF
                  </>
                ) : downloading === 'pdf' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'PDF'
                )}
              </button>
              <button
                onClick={() => downloadFile('docx')}
                disabled={isProcessing || downloading === 'docx'}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> DOCX
                  </>
                ) : downloading === 'docx' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'DOCX'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {children}
    </div>
  );
}

function VisualMarkdownEditor({ value, onChange, placeholder }) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const lastValueRef = useRef(value || "");
  const externalUpdateRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const editor = new ToastEditor({
      el: containerRef.current,
      height: "360px",
      initialEditType: "wysiwyg",
      hideModeSwitch: true,
      usageStatistics: false,
      autofocus: false,
      placeholder,
      initialValue: value || "",
      toolbarItems: [
        ["heading", "bold", "italic", "strike"],
        ["hr", "quote"],
        ["ul", "ol"],
        ["table", "link"],
      ],
    });
    editorRef.current = editor;
    const handleChange = () => {
      if (externalUpdateRef.current) return;
      const markdown = editor.getMarkdown();
      lastValueRef.current = markdown;
      onChange(markdown);
    };
    editor.on("change", handleChange);
    return () => {
      editor.off("change", handleChange);
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const nextValue = value || "";
    if (!editor || nextValue === lastValueRef.current) return;
    externalUpdateRef.current = true;
    editor.setMarkdown(nextValue, false);
    lastValueRef.current = nextValue;
    externalUpdateRef.current = false;
  }, [value]);

  return <div ref={containerRef} className="overflow-hidden rounded-lg border border-slate-300 bg-white" />;
}
